/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModalProps } from 'src/core/components/BasicModal';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { PhraseListModal } from 'src/luis/modules/features/components/PhraseListModal';
import { PhraseList } from 'src/luis/modules/features/types/PhraseList';

export type PhraseListModalContainerProps = BasicModalProps<PhraseList> & {
    /**
     * Initial data to populate the modal with.
     */
    initData?: Partial<PhraseList>;
};

const messages = defineMessages({
    addToastStart: {
        id: 'features.phraseListModalContainer.addToastStart',
        defaultMessage: 'Adding phrase list'
    },
    addToastSuccess: {
        id: 'features.phraseListModalContainer.addToastSuccess',
        defaultMessage: 'Add phrase list completed'
    },
    addToastFailed: {
        id: 'features.phraseListModalContainer.addToastFailed',
        defaultMessage: 'Add phrase list failed'
    },
    updateToastStart: {
        id: 'features.phraseListModalContainer.updateToastStart',
        defaultMessage: 'Updating phrase list feature'
    },
    updateToastSuccess: {
        id: 'features.phraseListModalContainer.updateToastSuccess',
        defaultMessage: 'Update phrase list completed'
    },
    updateToastFailed: {
        id: 'features.phraseListModalContainer.updateToastFailed',
        defaultMessage: 'Update phrase list failed'
    }
});

/**
 * Represents a container component for the phrase list creation modal.
 *
 * @param props The modal input properties.
 */
export const PhraseListModalContainer = withModalMounter(
    observer((props: PhraseListModalContainerProps) => {
        const { initData: modalInitData, onSubmit, onDismiss, hidden } = props;

        const { localize } = useLocalization();
        const { dispatcher, rootStore } = useGlobalState();

        const { userRole, displayedUserRole } = rootStore.serviceKey;

        const addOrUpdateTracker = useProgressTracker<PhraseList>({
            onComplete: phraseList => {
                onSubmit(phraseList);
            }
        });

        const initData = {
            ...({
                name: '',
                phrases: [] as string[],
                isExchangeable: true,
                id: null,
                isActive: true,
                isGlobal: true
            } as PhraseList),
            ...modalInitData
        };

        /**
         * Fires when add phrase list operation called
         *
         * @param phraseListDto The dto of the prase list to add
         */
        const addOrUpdate = (phraseList: PhraseList) => {
            if (initData.id) {
                dispatcher.dispatch('updatePhraseList', {
                    phraseList,
                    toastData: {
                        running: { title: localize(messages.updateToastStart) },
                        completed: { title: localize(messages.updateToastSuccess) },
                        failed: { title: localize(messages.updateToastFailed) }
                    },
                    tracker: addOrUpdateTracker
                });

                return;
            }

            dispatcher.dispatch('createPhraseList', {
                phraseList,
                toastData: {
                    running: { title: localize(messages.addToastStart) },
                    completed: { title: localize(messages.addToastSuccess) },
                    failed: { title: localize(messages.addToastFailed) }
                },
                tracker: addOrUpdateTracker
            });
        };

        return (
            <PhraseListModal
                hidden={hidden}
                initData={initData}
                onSubmit={addOrUpdate}
                onDismiss={onDismiss}
                disabled={addOrUpdateTracker.isRunning}
                userRole={userRole}
                displayedUserRole={displayedUserRole}
            />
        );
    })
);
